import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { Building2, Check, ChevronDown } from 'lucide-react';
import { useSession } from '../../auth/SessionContext';
import { useOrgs } from '../../hooks/useOrgs';
import { useOrgSwitch } from '../../hooks/useOrgSwitch';
import { cn } from '../../lib/utils';

/**
 * Nav org switcher (ADR-0010 Phase 2, task 4; always-visible per the org
 * consolidation follow-up). Renders in the sidebar header — the current
 * org's name is ALWAYS shown, so the user always knows which org they're
 * in, even now that org consolidation means most identities have exactly
 * one membership:
 *
 * - Single org (or the org count is still loading): a non-interactive
 *   label — no dropdown affordance, no `GET /api/orgs` listbox chrome.
 * - 2+ orgs: the original interactive button that opens a listbox of the
 *   identity's other ACTIVE memberships, unchanged.
 */
export function OrgSwitcher() {
  const { org } = useSession();
  const { orgs, isLoading, refresh } = useOrgs();
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const { switchTo, isSwitching, error } = useOrgSwitch({
    onError: () => {
      void refresh();
    },
  });

  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      itemRefs.current[activeIndex]?.focus();
    }
  }, [isOpen, activeIndex]);

  // Single-org (or not-yet-known) identities see a plain, non-interactive
  // label — always visible, but no switcher/listbox chrome since there is
  // nothing to switch to.
  if (isLoading || orgs.length < 2) {
    // Nothing to show until the org name resolves — avoids a momentary
    // empty label (and an empty "Current organization: " announcement)
    // during the brief session-loading window.
    if (!org.name) {
      return null;
    }
    return (
      <div
        className="flex min-w-0 max-w-[10rem] items-center gap-1.5 px-2 py-1.5 text-sm font-medium text-gray-500 sm:max-w-[12rem]"
        aria-label={`Current organization: ${org.name}`}
      >
        <Building2 className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
        <span className="truncate">{org.name}</span>
      </div>
    );
  }

  function openMenu() {
    const currentIndex = orgs.findIndex((o) => o.orgId === org.orgId);
    setActiveIndex(currentIndex >= 0 ? currentIndex : 0);
    setIsOpen(true);
  }

  function handleTriggerKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (!isOpen && (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      openMenu();
    }
  }

  function handleListKeyDown(event: KeyboardEvent<HTMLUListElement>) {
    if (event.key === 'Escape') {
      event.preventDefault();
      setIsOpen(false);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((prev) => Math.min(prev + 1, orgs.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
    } else if (event.key === 'Tab') {
      setIsOpen(false);
    }
  }

  return (
    <div ref={containerRef} className="relative min-w-0">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`Current organization: ${org.name}. Switch organization`}
        onClick={() => (isOpen ? setIsOpen(false) : openMenu())}
        onKeyDown={handleTriggerKeyDown}
        disabled={isSwitching}
        className="flex max-w-[10rem] items-center gap-1 rounded-md px-2 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-60 sm:max-w-[12rem]"
      >
        <span className="truncate">{org.name}</span>
        <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
      </button>

      {isOpen && (
        <ul
          role="listbox"
          aria-label="Organizations"
          onKeyDown={handleListKeyDown}
          className="absolute left-0 top-full z-50 mt-1 w-64 rounded-md border border-gray-200 bg-white py-1 shadow-lg"
        >
          {orgs.map((candidate, index) => {
            const isCurrent = candidate.orgId === org.orgId;
            return (
              <li key={candidate.orgId} role="presentation">
                <button
                  ref={(el) => {
                    itemRefs.current[index] = el;
                  }}
                  type="button"
                  role="option"
                  aria-selected={isCurrent}
                  tabIndex={-1}
                  onClick={() => void switchTo(candidate.orgId)}
                  disabled={isSwitching}
                  className={cn(
                    'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-gray-50 disabled:opacity-60',
                    isCurrent ? 'font-medium text-gray-900' : 'text-gray-600',
                  )}
                >
                  <span className="truncate">{candidate.name}</span>
                  {isCurrent && <Check className="h-4 w-4 shrink-0 text-blue-600" aria-hidden="true" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {error && (
        <div
          role="alert"
          className="absolute left-0 top-full z-50 mt-1 w-64 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
        >
          {error}
        </div>
      )}

      {isSwitching && (
        <span role="status" className="sr-only">
          Switching organization…
        </span>
      )}
    </div>
  );
}
